import { sendChatMessage } from '@/lib/api/chat';
import { useCallback, useRef, useState } from 'react';
export function useChat() {
    const [messages, setMessages] = useState([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const abortRef = useRef(null);
    const sendMessage = useCallback(async (text) => {
        if (!text.trim() || isStreaming)
            return;
        const userMsg = {
            id: crypto.randomUUID(),
            role: 'user',
            content: text.trim(),
            timestamp: new Date().toISOString(),
            status: 'complete',
        };
        const assistantId = crypto.randomUUID();
        const assistantMsg = {
            id: assistantId,
            role: 'assistant',
            content: '',
            timestamp: new Date().toISOString(),
            status: 'streaming',
        };
        setMessages((prev) => [...prev, userMsg, assistantMsg]);
        setIsStreaming(true);
        const controller = new AbortController();
        abortRef.current = controller;
        const onEvent = (event) => {
            switch (event.type) {
                case 'token':
                    setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, content: m.content + (event.content ?? '') } : m));
                    break;
                case 'data':
                    setMessages((prev) => prev.map((m) => m.id === assistantId
                        ? {
                            ...m,
                            data: {
                                columns: event.columns ?? [],
                                rows: event.rows ?? [],
                                totalCount: event.totalCount,
                            },
                        }
                        : m));
                    break;
                case 'done':
                    if (event.sessionId)
                        setSessionId(event.sessionId);
                    setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, status: 'complete' } : m)));
                    break;
                case 'error':
                    setMessages((prev) => prev.map((m) => m.id === assistantId
                        ? { ...m, status: 'error', content: event.error ?? 'Something went wrong.' }
                        : m));
                    break;
            }
        };
        try {
            await sendChatMessage({ message: text.trim(), sessionId: sessionId ?? undefined }, onEvent, controller.signal);
        }
        catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
                setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, status: 'complete' } : m)));
            }
            else {
                setMessages((prev) => prev.map((m) => m.id === assistantId
                    ? { ...m, status: 'error', content: 'Failed to get a response.' }
                    : m));
            }
        }
        finally {
            setIsStreaming(false);
            abortRef.current = null;
        }
    }, [isStreaming, sessionId]);
    const cancel = useCallback(() => {
        abortRef.current?.abort();
    }, []);
    const clearSession = useCallback(() => {
        cancel();
        setMessages([]);
        setSessionId(null);
    }, [cancel]);
    return { messages, isStreaming, sessionId, sendMessage, cancel, clearSession };
}
